import styled from 'styled-components';

import { spacing, colors } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { createAnimationOnAppear } from 'utils/styledComponentHelpers';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';

import {
  GridArea,
  RECENT_EVENTS_ANIMATION_STAGGER_DELAY_MS,
  RECENT_EVENTS_POLLING_INTERVAL_MS,
  RECENT_EVENTS_ROW_COUNT,
} from '../constants';
import { Event } from './Event';
import { RefetchSpinner } from './RefetchSpinner';
import { PlaceholderListItem } from './PlaceholderListItem';
import { ErrorPlaceholder } from './ErrorPlaceholder';
import { useEventHistoryQuery } from '../../hooks/useEventHistoryQuery';

const displayText = {
  incompleteListPlaceholder: 'This user has no other events.',
  emptyListPlaceholder: 'This user has no events.',
};

const BASE_QUERY_OPTIONS = {
  sortBy: 'createdAt',
  sortDirection: SORT_DIRECTIONS.DESCENDING,
  from: 0,
  limit: RECENT_EVENTS_ROW_COUNT,
  eventTypes: [],
  userAgents: [],
  customEvents: [],
  filter: '',
};

const showLineOnMount = createAnimationOnAppear('showLineOnMount', `opacity: 0;`, `opacity: 1;`);
const Line = styled.li`
  width: ${spacing.eighth};
  background-color: ${colors.grey5};
  height: auto;
  top: ${spacing.base};
  left: ${pxToRem(30)};
  bottom: ${spacing.base};
  ${showLineOnMount}
  animation-delay: ${RECENT_EVENTS_ANIMATION_STAGGER_DELAY_MS}ms;
`;

const Wrapper = styled.ul`
  border-radius: ${spacing.half};
  border: ${spacing.single} solid ${colors.grey15};
  width: ${pxToRem(425)};
  padding: ${spacing.base};
  margin: 0;
  list-style: none;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(${RECENT_EVENTS_ROW_COUNT}, ${pxToRem(30)});
  grid-gap: ${spacing.base};
  position: relative;
  align-items: center;
  grid-area: ${GridArea.recentEventsList};

  & li {
    position: relative;
    z-index: 1;
  }

  & ${Line} {
    position: absolute;
    z-index: 0;
  }
`;

export const EventsList = () => {
  const {
    data: { jsonEvents: events = [] } = {},
    isLoading,
    isError,
    refetch,
  } = useEventHistoryQuery(
    {
      ...BASE_QUERY_OPTIONS,
    },
    RECENT_EVENTS_POLLING_INTERVAL_MS,
  );

  return (
    <Wrapper data-test="events-list">
      {!isLoading &&
        !isError &&
        events.map((event, i) => <Event key={`event-ID:${i}-${event.createdAt}`} event={event} />)}
      {!isLoading && !isError && events.length < RECENT_EVENTS_ROW_COUNT && (
        <PlaceholderListItem
          $rowSpan={RECENT_EVENTS_ROW_COUNT - events.length}
          data-test="non-full-list-text"
        >
          {events.length ? displayText.incompleteListPlaceholder : displayText.emptyListPlaceholder}
        </PlaceholderListItem>
      )}
      {!isLoading && !isError && !!events.length && <Line />}
      {!isLoading && isError && <ErrorPlaceholder onReloadButtonClick={() => refetch()} />}
      {isLoading && <RefetchSpinner />}
    </Wrapper>
  );
};
